import { Box, Container, Typography, Button, Grid } from "@mui/material";
import { Link } from "react-router-dom";
import { useDocumentTitle } from "../../utils/data";
import { keyframes } from "@mui/system";

const floatBall = keyframes`
  0%, 100% { transform: translate(0, 0); }
  25% { transform: translate(20px, -20px); }
  50% { transform: translate(-10px, 20px); }
  75% { transform: translate(10px, -10px); }
`;

const scaleInCenter = keyframes`
  from { opacity: 0; transform: scale(0.85); }
  to { opacity: 1; transform: scale(1); }
`;

const moveCircle1 = keyframes`
  0%, 100% {
    transform: translate(0, 0);
  }
  25% {
    transform: translate(30px, -40px);
  }
  50% {
    transform: translate(-20px, -30px);
  }
  75% {
    transform: translate(40px, 20px);
  }
`;

const moveCircle2 = keyframes`
  0%, 100% {
    transform: translate(0, 0);
  }
  25% {
    transform: translate(-40px, 20px);
  }
  50% {
    transform: translate(30px, 40px);
  }
  75% {
    transform: translate(-30px, -40px);
  }
`;

const moveCircle3 = keyframes`
  0%, 100% {
    transform: translate(0, 0);
  }
  25% {
    transform: translate(20px, 50px);
  }
  50% {
    transform: translate(-40px, 30px);
  }
  75% {
    transform: translate(40px, -20px);
  }
`;

const serviceOfferings = [
  {
    icon: "📱",
    title: "Native App Development",
    desc: "Build high-performance iOS and Android apps with platform-specific features and native APIs."
  },
  {
    icon: "🔄",
    title: "Cross-Platform Solutions",
    desc: "Develop once and deploy to multiple platforms with React Native and Flutter technologies."
  },
  {
    icon: "🧪",
    title: "QA & Testing",
    desc: "Comprehensive testing across devices, operating systems, and network conditions for reliability."
  },
  {
    icon: "🚀",
    title: "App Launch & Deployment",
    desc: "Seamless deployment to App Store and Play Store with all compliance and optimization."
  },
  {
    icon: "📊",
    title: "Analytics & Monitoring",
    desc: "Real-time analytics, crash reporting, and performance monitoring for continuous improvement."
  },
  {
    icon: "🛠️",
    title: "Maintenance & Support",
    desc: "Ongoing support, updates, and enhancements to keep your app secure and competitive."
  }
];

const whyChooseUs = [
  {
    icon: "📲",
    title: "Expert Mobile Team",
    desc: "Specialists in iOS, Android, React Native, and Flutter with years of mobile expertise."
  },
  {
    icon: "🎨",
    title: "User-Centric Design",
    desc: "Beautiful, intuitive interfaces designed for optimal mobile user experience and engagement."
  },
  {
    icon: "⚡",
    title: "Performance Focused",
    desc: "Apps optimized for speed, battery efficiency, and seamless performance on all devices."
  },
  {
    icon: "🔒",
    title: "Security First",
    desc: "Enterprise-grade security, data encryption, and compliance with mobile security standards."
  }
];

const mobileExpertise = [
  {
    title: "iOS Development",
    desc: "Native iOS apps using Swift, Objective-C, and latest iOS frameworks for iPhone and iPad."
  },
  {
    title: "Android Development",
    desc: "High-performance Android apps with Kotlin and Java for smartphones and tablets."
  },
  {
    title: "React Native",
    desc: "Cross-platform mobile apps with code reuse and near-native performance."
  },
  {
    title: "Flutter Development",
    desc: "Beautiful, fast apps using Flutter for iOS, Android, and web platforms."
  },
  {
    title: "Progressive Web Apps",
    desc: "Mobile-first web apps with app-like experience and offline functionality."
  }
];

const MobileAppDevelopment = () => {
  useDocumentTitle("Briozon - Mobile Application Development");

  return (
    <Box>
      {/* Hero Banner */}
      <Box
        sx={{
          background: "linear-gradient(135deg, #f8fafc 0%, #f0f4ff 100%)",
          py: { xs: 6, sm: 8, md: 10 },
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Animated background circles */}
        <Box
          sx={{
            position: "absolute",
            top: "5%",
            right: "10%",
            width: 150,
            height: 150,
            borderRadius: "50%",
            background: "radial-gradient(circle, rgba(91, 124, 250, 0.15) 0%, rgba(91, 124, 250, 0) 70%)",
            animation: `${moveCircle1} 15s ease-in-out infinite`,
            pointerEvents: "none",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            bottom: "10%",
            left: "5%",
            width: 200,
            height: 200,
            borderRadius: "50%",
            background: "radial-gradient(circle, rgba(118, 75, 162, 0.12) 0%, rgba(118, 75, 162, 0) 70%)",
            animation: `${moveCircle2} 18s ease-in-out infinite`,
            pointerEvents: "none",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            top: "40%",
            right: "5%",
            width: 120,
            height: 120,
            borderRadius: "50%",
            background: "radial-gradient(circle, rgba(91, 124, 250, 0.1) 0%, rgba(91, 124, 250, 0) 70%)",
            animation: `${moveCircle3} 20s ease-in-out infinite`,
            pointerEvents: "none",
          }}
        />

        {/* Floating decorative elements */}
        <Box
          sx={{
            position: "absolute",
            top: "10%",
            right: "5%",
            width: 120,
            height: 120,
            borderRadius: "50%",
            background: "linear-gradient(135deg, rgba(91,124,250,0.1) 0%, rgba(118,75,162,0.1) 100%)",
            animation: `${floatBall} 8s ease-in-out infinite`,
          }}
        />
        <Box
          sx={{
            position: "absolute",
            bottom: "15%",
            left: "3%",
            width: 80,
            height: 80,
            borderRadius: "50%",
            background: "linear-gradient(135deg, rgba(118,75,162,0.1) 0%, rgba(91,124,250,0.1) 100%)",
            animation: `${floatBall} 6s ease-in-out infinite 1s`,
          }}
        />

        <Container maxWidth="lg">
          <Grid container spacing={{ xs: 4, sm: 6, md: 8 }} alignItems="center">
            {/* Left Content */}
            <Grid item xs={12} md={6}>
              <Box>
                <Typography
                  variant="overline"
                  sx={{
                    fontSize: { xs: "12px", sm: "14px" },
                    fontWeight: 700,
                    color: "#5b7cfa",
                    letterSpacing: "2px",
                    mb: 1,
                    display: "block",
                  }}
                >
                  MOBILE APPLICATION DEVELOPMENT
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { xs: "32px", sm: "42px", md: "48px" },
                    fontWeight: 800,
                    color: "#1b1f33",
                    lineHeight: 1.2,
                    mb: 2,
                  }}
                >
                  Mobile App{" "}
                  <Box
                    component="span"
                    sx={{
                      background: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      backgroundClip: "text",
                    }}
                  >
                    Solutions
                  </Box>
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontSize: { xs: "16px", sm: "19px", md: "20px" },
                    color: "#4c5472",
                    lineHeight: 1.8,
                    mb: 3,
                    textAlign: "justify",
                  }}
                >
                  Transform your business with powerful, user-centric mobile applications. From concept to launch, we develop innovative iOS and Android apps that engage users and drive business growth.
                </Typography>
                <Button
                  component={Link}
                  to="/contact"
                  variant="contained"
                  sx={{
                    background: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)",
                    color: "#fff",
                    fontWeight: 700,
                    px: 4,
                    py: 1.5,
                    borderRadius: "12px",
                    fontSize: { xs: "16px", sm: "19px", md: "20px" },
                    textTransform: "none",
                    boxShadow: "0 8px 24px rgba(91, 124, 250, 0.3)",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: "0 12px 32px rgba(91, 124, 250, 0.4)",
                    },
                  }}
                >
                  Get Started
                </Button>
              </Box>
            </Grid>

            {/* Right - Creative Visual */}
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  position: "relative",
                  height: { xs: 320, sm: 400, md: 450 },
                  animation: `${scaleInCenter} 0.8s ease-out 0.2s both`,
                }}
              >
                {/* Main gradient circle */}
                <Box
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: { xs: 220, sm: 280, md: 320 },
                    height: { xs: 220, sm: 280, md: 320 },
                    borderRadius: "50%",
                    background: "linear-gradient(135deg, rgba(91,124,250,0.15) 0%, rgba(118,75,162,0.15) 100%)",
                    filter: "blur(40px)",
                  }}
                />

                {/* Central icon */}
                <Box
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: { xs: 100, sm: 120 },
                    height: { xs: 100, sm: 120 },
                    borderRadius: "24px",
                    background: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: { xs: "48px", sm: "56px" },
                    boxShadow: "0 20px 40px rgba(91, 124, 250, 0.4)",
                    animation: `${floatBall} 6s ease-in-out infinite`,
                    zIndex: 2,
                  }}
                >
                  📱
                </Box>

                {/* Floating feature cards */}
                <Box
                  sx={{
                    position: "absolute",
                    top: "8%",
                    left: "10%",
                    background: "#fff",
                    borderRadius: "16px",
                    px: 2.5,
                    py: 1.5,
                    boxShadow: "0 8px 24px rgba(0,0,0,0.1)",
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    animation: `${floatBall} 7s ease-in-out infinite 0.5s`,
                    zIndex: 1,
                  }}
                >
                  <Box sx={{ fontSize: "24px" }}>🍎</Box>
                  <Typography sx={{ fontWeight: 600, color: "#1b1f33", fontSize: "13px" }}>
                    iOS
                  </Typography>
                </Box>

                <Box
                  sx={{
                    position: "absolute",
                    top: "15%",
                    right: "5%",
                    background: "#fff",
                    borderRadius: "16px",
                    px: 2.5,
                    py: 1.5,
                    boxShadow: "0 8px 24px rgba(0,0,0,0.1)",
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    animation: `${floatBall} 8s ease-in-out infinite 1s`,
                    zIndex: 1,
                  }}
                >
                  <Box sx={{ fontSize: "24px" }}>🤖</Box>
                  <Typography sx={{ fontWeight: 600, color: "#1b1f33", fontSize: "13px" }}>
                    Android
                  </Typography>
                </Box>

                <Box
                  sx={{
                    position: "absolute",
                    bottom: "20%",
                    left: "5%",
                    background: "#fff",
                    borderRadius: "16px",
                    px: 2.5,
                    py: 1.5,
                    boxShadow: "0 8px 24px rgba(0,0,0,0.1)",
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    animation: `${floatBall} 9s ease-in-out infinite 1.5s`,
                    zIndex: 1,
                  }}
                >
                  <Box sx={{ fontSize: "24px" }}>⚛️</Box>
                  <Typography sx={{ fontWeight: 600, color: "#1b1f33", fontSize: "13px" }}>
                    React Native
                  </Typography>
                </Box>

                <Box
                  sx={{
                    position: "absolute",
                    bottom: "10%",
                    right: "10%",
                    background: "#fff",
                    borderRadius: "16px",
                    px: 2.5,
                    py: 1.5,
                    boxShadow: "0 8px 24px rgba(0,0,0,0.1)",
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    animation: `${floatBall} 6s ease-in-out infinite 2s`,
                    zIndex: 1,
                  }}
                >
                  <Box sx={{ fontSize: "24px" }}>🦋</Box>
                  <Typography sx={{ fontWeight: 600, color: "#1b1f33", fontSize: "13px" }}>
                    Flutter
                  </Typography>
                </Box>

                {/* Decorative rings */}
                <Box
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: { xs: 200, sm: 260 },
                    height: { xs: 200, sm: 260 },
                    borderRadius: "50%",
                    border: "2px dashed rgba(91, 124, 250, 0.2)",
                    animation: `${floatBall} 10s ease-in-out infinite`,
                  }}
                />
                <Box
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: { xs: 280, sm: 360 },
                    height: { xs: 280, sm: 360 },
                    borderRadius: "50%",
                    border: "1px dashed rgba(118, 75, 162, 0.15)",
                    animation: `${floatBall} 12s ease-in-out infinite reverse`,
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Section 2 — Overview: text + platform checklist */}
      <Box sx={{ py: { xs: 8, md: 10 }, backgroundColor: "#fff" }}>
        <Container maxWidth="lg">
          <Grid container spacing={{ xs: 4, md: 8 }} alignItems="flex-start">
            <Grid item xs={12} md={7}>
              <Typography variant="overline" sx={{ fontSize: "13px", fontWeight: 700, color: "#5b7cfa", letterSpacing: "2px", mb: 1, display: "block" }}>
                MOBILE DEVELOPMENT
              </Typography>
              <Typography variant="h3" sx={{ fontWeight: 800, fontSize: { xs: "26px", sm: "34px", md: "38px" }, color: "#1b1f33", mb: 3, lineHeight: 1.2 }}>
                Mobile Development{" "}
                <Box component="span" sx={{ background: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" }}>
                  Technologies & Expertise
                </Box>
              </Typography>
              <Typography variant="body1" sx={{ fontSize: { xs: "16px", sm: "17px" }, color: "#4c5472", lineHeight: 1.85, mb: 3, textAlign: "justify" }}>
                We develop high-performance native iOS and Android applications optimized for speed, user experience, and battery efficiency. Our team leverages Swift, Kotlin, and the latest mobile frameworks to create apps that users love. With expertise in App Store and Google Play deployment, we ensure your app reaches millions of potential users.
              </Typography>
              <Typography variant="body1" sx={{ fontSize: { xs: "16px", sm: "17px" }, color: "#4c5472", lineHeight: 1.85, textAlign: "justify" }}>
                Cross-platform development using React Native and Flutter allows us to build beautiful, performant apps with maximum code reuse. These modern frameworks deliver near-native performance while significantly reducing development time and costs. Perfect for businesses targeting both iOS and Android markets efficiently.
              </Typography>
            </Grid>
            <Grid item xs={12} md={5}>
              <Box sx={{ background: "linear-gradient(135deg, #f8faff 0%, #f0f4ff 100%)", borderRadius: "20px", p: { xs: 3, md: 4 }, border: "1px solid rgba(91,124,250,0.12)" }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#1b1f33", mb: 3, fontSize: "13px", letterSpacing: "2px", textTransform: "uppercase" }}>
                  Mobile Platforms
                </Typography>
                {["iOS (Swift / Objective-C)", "Android (Kotlin / Java)", "React Native", "Flutter", "Kotlin Multiplatform", "Progressive Web Apps"].map((platform) => (
                  <Box key={platform} sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
                    <Box sx={{ width: 28, height: 28, borderRadius: "8px", background: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      <Typography sx={{ color: "#fff", fontSize: "12px", fontWeight: 700 }}>✓</Typography>
                    </Box>
                    <Typography sx={{ fontSize: { xs: "15px", sm: "16px" }, color: "#4c5472", fontWeight: 500 }}>{platform}</Typography>
                  </Box>
                ))}
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Section 3 — Capabilities: 3 tiles + 2-col block */}
      <Box sx={{ py: { xs: 8, md: 10 }, backgroundColor: "#fafbff" }}>
        <Container maxWidth="lg">
          <Box sx={{ mb: { xs: 5, md: 6 } }}>
            <Typography variant="overline" sx={{ fontSize: "13px", fontWeight: 700, color: "#5b7cfa", letterSpacing: "2px", mb: 1, display: "block" }}>
              CAPABILITIES
            </Typography>
            <Typography variant="h3" sx={{ fontWeight: 800, fontSize: { xs: "26px", sm: "34px", md: "38px" }, color: "#1b1f33", mb: 2, lineHeight: 1.2 }}>
              Our Mobile{" "}
              <Box component="span" sx={{ background: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" }}>
                Development Capabilities
              </Box>
            </Typography>
            <Typography variant="body1" sx={{ fontSize: { xs: "16px", sm: "17px" }, color: "#4c5472", lineHeight: 1.85, maxWidth: "700px", textAlign: "justify" }}>
              Briozon specializes in comprehensive mobile application development across iOS, Android, and cross-platform solutions. We deliver apps that engage users, drive downloads, and generate revenue through cutting-edge technology and user-centric design.
            </Typography>
          </Box>

          <Box sx={{ mb: { xs: 6, md: 8 } }}>
          <Grid container spacing={3}>
            {[
              { icon: "⚡", title: "Native Performance", desc: "Apps built with platform-specific frameworks for maximum speed, responsiveness, and battery efficiency on every device." },
              { icon: "🏪", title: "App Store Deployment", desc: "Full support for App Store and Google Play submission, compliance, optimization, and post-launch monitoring." },
              { icon: "🔄", title: "Real-Time Features", desc: "Live data sync, push notifications, WebSocket integration, and real-time collaboration features built-in." },
            ].map((item) => (
              <Grid item xs={12} sm={4} key={item.title} sx={{ display: "flex", flexDirection: "column" }}>
                <Box sx={{ p: { xs: 3, md: 4 }, borderRadius: "16px", background: "#fff", border: "1px solid rgba(91,124,250,0.1)", flexGrow: 1, transition: "all 0.3s ease", "&:hover": { transform: "translateY(-6px)", boxShadow: "0 16px 40px rgba(91,124,250,0.12)", borderColor: "rgba(91,124,250,0.3)" } }}>
                  <Box sx={{ width: 56, height: 56, borderRadius: "14px", background: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.6rem", mb: 2.5, boxShadow: "0 6px 16px rgba(91,124,250,0.25)" }}>
                    {item.icon}
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: "#1b1f33", mb: 1, fontSize: { xs: "18px", sm: "20px" } }}>
                    {item.title}
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: { xs: "15px", sm: "16px" }, color: "#4c5472", lineHeight: 1.75 }}>
                    {item.desc}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
          </Box>

          <Grid container spacing={{ xs: 4, md: 8 }} alignItems="flex-start">
            <Grid item xs={12} md={5}>
              <Box sx={{ background: "linear-gradient(135deg, #f8faff 0%, #f0f4ff 100%)", borderRadius: "20px", p: { xs: 3, md: 4 }, border: "1px solid rgba(91,124,250,0.12)" }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#1b1f33", mb: 3, fontSize: "13px", letterSpacing: "2px", textTransform: "uppercase" }}>
                  Key Strengths
                </Typography>
                {[
                  { icon: "🌐", label: "Cross-Platform Development" },
                  { icon: "🎨", label: "UX/UI Design Excellence" },
                  { icon: "📊", label: "Analytics & Monitoring" },
                ].map((f) => (
                  <Box key={f.label} sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2.5 }}>
                    <Box sx={{ width: 44, height: 44, borderRadius: "12px", background: "linear-gradient(135deg, rgba(91,124,250,0.12) 0%, rgba(118,75,162,0.12) 100%)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.3rem", flexShrink: 0 }}>
                      {f.icon}
                    </Box>
                    <Typography sx={{ fontSize: { xs: "15px", sm: "16px" }, color: "#4c5472", fontWeight: 600 }}>{f.label}</Typography>
                  </Box>
                ))}
              </Box>
            </Grid>
            <Grid item xs={12} md={7}>
              <Typography variant="h4" sx={{ fontWeight: 800, fontSize: { xs: "22px", sm: "28px", md: "32px" }, color: "#1b1f33", mb: 3, lineHeight: 1.3 }}>
                Building{" "}
                <Box component="span" sx={{ background: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" }}>
                  Engaging Mobile Experiences
                </Box>
              </Typography>
              <Typography variant="body1" sx={{ fontSize: { xs: "16px", sm: "17px" }, color: "#4c5472", lineHeight: 1.85, mb: 3, textAlign: "justify" }}>
                We create mobile applications that combine stunning design with powerful functionality. Every app we build is optimized for performance, usability, and monetization. From concept to launch, we handle every aspect of mobile app development.
              </Typography>
              <Typography variant="body1" sx={{ fontSize: { xs: "16px", sm: "17px" }, color: "#4c5472", lineHeight: 1.85, textAlign: "justify" }}>
                Our team stays current with the latest mobile technologies and best practices. We build apps that scale with your business, adapt to market demands, and continuously engage your users. Whether you need a startup MVP or enterprise-grade solution, we deliver results.
              </Typography>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Section 4 — Mobile Expertise */}
      <Box sx={{ py: { xs: 8, md: 10 }, background: "linear-gradient(135deg, #eef1ff 0%, #f3eeff 100%)" }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: "center", mb: { xs: 5, md: 6 } }}>
            <Typography variant="overline" sx={{ fontSize: "13px", fontWeight: 700, color: "#5b7cfa", letterSpacing: "2px", mb: 1, display: "block" }}>
              EXPERTISE
            </Typography>
            <Typography variant="h3" sx={{ fontWeight: 800, fontSize: { xs: "26px", sm: "34px", md: "38px" }, color: "#1b1f33", mb: 2, lineHeight: 1.2 }}>
              Our{" "}
              <Box component="span" sx={{ background: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" }}>
                Mobile Expertise
              </Box>
            </Typography>
            <Typography variant="body1" sx={{ color: "#4c5472", lineHeight: 1.8, maxWidth: 700, mx: "auto", fontSize: { xs: "16px", sm: "17px" } }}>
              Expertise across all major mobile platforms and frameworks to deliver your app vision.
            </Typography>
          </Box>
          <Grid container spacing={3}>
            {mobileExpertise.map((item, index) => (
              <Grid item xs={12} sm={6} md={4} key={index} sx={{ display: "flex", flexDirection: "column" }}>
                <Box sx={{ p: { xs: 3, md: 4 }, borderRadius: "16px", background: "#fff", borderTop: "1px solid rgba(91,124,250,0.1)", borderRight: "1px solid rgba(91,124,250,0.1)", borderBottom: "1px solid rgba(91,124,250,0.1)", borderLeft: "4px solid #5b7cfa", flexGrow: 1, transition: "all 0.3s ease", "&:hover": { background: "#fafbff", transform: "translateY(-4px)", boxShadow: "0 16px 40px rgba(91,124,250,0.14)" } }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
                    <Box sx={{ width: 36, height: 36, borderRadius: "10px", background: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      <Typography sx={{ color: "#fff", fontSize: "13px", fontWeight: 700 }}>{String(index + 1).padStart(2, "0")}</Typography>
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: "#1b1f33", fontSize: { xs: "17px", sm: "18px" } }}>
                      {item.title}
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ fontSize: { xs: "15px", sm: "16px" }, color: "#4c5472", lineHeight: 1.75 }}>
                    {item.desc}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Section 5 — Why Choose Us: horizontal icon + text rows */}
      <Box sx={{ py: { xs: 8, md: 10 }, backgroundColor: "#fff" }}>
        <Container maxWidth="lg">
          <Box sx={{ mb: { xs: 5, md: 6 } }}>
            <Typography variant="overline" sx={{ fontSize: "13px", fontWeight: 700, color: "#5b7cfa", letterSpacing: "2px", mb: 1, display: "block" }}>
              WHY BRIOZON
            </Typography>
            <Typography variant="h3" sx={{ fontWeight: 800, fontSize: { xs: "26px", sm: "34px", md: "38px" }, color: "#1b1f33", mb: 2, lineHeight: 1.2 }}>
              Why Choose Briozon for{" "}
              <Box component="span" sx={{ background: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" }}>
                Mobile Apps
              </Box>
            </Typography>
          </Box>
          <Grid container spacing={3}>
            {whyChooseUs.map((item, index) => (
              <Grid item xs={12} sm={6} key={index} sx={{ display: "flex", flexDirection: "column" }}>
                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 3, p: { xs: 3, md: 4 }, borderRadius: "16px", border: "1px solid rgba(91,124,250,0.1)", backgroundColor: "#fafbff", flexGrow: 1, transition: "all 0.3s ease", "&:hover": { transform: "translateY(-4px)", boxShadow: "0 12px 32px rgba(91,124,250,0.12)", borderColor: "rgba(91,124,250,0.3)", backgroundColor: "#fff" } }}>
                  <Box sx={{ width: 58, height: 58, borderRadius: "16px", background: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.7rem", flexShrink: 0, boxShadow: "0 6px 16px rgba(91,124,250,0.25)" }}>
                    {item.icon}
                  </Box>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: "#1b1f33", mb: 0.75, fontSize: { xs: "18px", sm: "20px" } }}>
                      {item.title}
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: { xs: "15px", sm: "16px" }, color: "#4c5472", lineHeight: 1.75 }}>
                      {item.desc}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Section 6 — Service Offerings: top-border gradient cards */}
      <Box sx={{ py: { xs: 8, md: 10 }, background: "linear-gradient(135deg, #f8faff 0%, #f0f4ff 100%)" }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: "center", mb: { xs: 5, md: 6 } }}>
            <Typography variant="overline" sx={{ fontSize: "13px", fontWeight: 700, color: "#5b7cfa", letterSpacing: "2px", mb: 1, display: "block" }}>
              SERVICES
            </Typography>
            <Typography variant="h3" sx={{ fontWeight: 800, fontSize: { xs: "26px", sm: "34px", md: "38px" }, color: "#1b1f33", mb: 2, lineHeight: 1.2 }}>
              Our Mobile App{" "}
              <Box component="span" sx={{ background: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" }}>
                Development Services
              </Box>
            </Typography>
            <Typography variant="body1" sx={{ color: "#4c5472", lineHeight: 1.8, maxWidth: 700, mx: "auto", fontSize: { xs: "16px", sm: "17px" } }}>
              Complete mobile app development lifecycle from strategy to post-launch support.
            </Typography>
          </Box>
          <Grid container spacing={3}>
            {serviceOfferings.map((item, index) => (
              <Grid item xs={12} sm={6} md={4} key={index} sx={{ display: "flex", flexDirection: "column" }}>
                <Box sx={{ p: { xs: 3, md: 4 }, borderRadius: "16px", background: "#fff", borderTop: "4px solid #5b7cfa", boxShadow: "0 2px 12px rgba(91,124,250,0.07)", flexGrow: 1, transition: "all 0.3s ease", "&:hover": { transform: "translateY(-6px)", boxShadow: "0 16px 40px rgba(91,124,250,0.14)" } }}>
                  <Box sx={{ fontSize: "2rem", mb: 2 }}>{item.icon}</Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: "#1b1f33", mb: 1, fontSize: { xs: "18px", sm: "20px" } }}>
                    {item.title}
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: { xs: "15px", sm: "16px" }, color: "#4c5472", lineHeight: 1.75 }}>
                    {item.desc}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Contact Section */}
      <Box
        sx={{
          py: { xs: 8, sm: 10, md: 12 },
          background: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Decorative circles */}
        <Box
          sx={{
            position: "absolute",
            top: "-50px",
            right: "-50px",
            width: 200,
            height: 200,
            borderRadius: "50%",
            background: "rgba(255,255,255,0.1)",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            bottom: "-30px",
            left: "-30px",
            width: 150,
            height: 150,
            borderRadius: "50%",
            background: "rgba(255,255,255,0.08)",
          }}
        />

        <Container maxWidth="md">
          <Box sx={{ textAlign: "center", position: "relative", zIndex: 1 }}>
            <Typography
              variant="h3"
              sx={{
                fontSize: { xs: "28px", sm: "36px", md: "42px" },
                fontWeight: 800,
                color: "#fff",
                mb: 2,
              }}
            >
              Ready to Launch Your Mobile App?
            </Typography>
            <Typography
              variant="h6"
              sx={{
                fontSize: { xs: "16px", sm: "19px", md: "20px" },
                color: "rgba(255,255,255,0.9)",
                mb: 4,
                maxWidth: "700px",
                mx: "auto",
                lineHeight: 1.8,
              }}
            >
              Let's turn your mobile app idea into reality. Schedule a consultation with our mobile development experts and discuss your project requirements, target platforms, and business goals.
            </Typography>
            <Button
              component="a"
              href="/contact"
              variant="contained"
              sx={{
                backgroundColor: "#fff",
                color: "#5b7cfa",
                fontWeight: 700,
                px: 5,
                py: 1.5,
                borderRadius: "12px",
                fontSize: { xs: "16px", sm: "19px", md: "20px" },
                textTransform: "none",
                transition: "all 0.3s ease",
                boxShadow: "0 8px 24px rgba(91, 124, 250, 0.08)",
                "&:hover": {
                  backgroundColor: "#f0f0f0",
                  transform: "translateY(-4px)",
                  boxShadow: "0 12px 24px rgba(0,0,0,0.12)",
                },
              }}
            >
              Let's Connect
            </Button>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default MobileAppDevelopment;
